import { expectArray, expectBoolean, expectNullable, expectNumber, expectOptionalString, expectRecord, expectString } from "./schema";
import { parseMemberStatus } from "./admin-directory";
export const STUDENT_STATUSES = ["active", "inactive"];
export const FAMILY_ACCOUNT_STATUSES = ["active", "inactive"];
export const ENROLLMENT_TYPES = ["trial", "regular"];
export const ENROLLMENT_STATUSES = [
    "pendingStart",
    "active",
    "paused",
    "ended",
    "cancelled"
];
function parseStringEnum(value, allowedValues, path) {
    const parsed = expectString(value, path);
    if (!allowedValues.includes(parsed)) {
        throw new Error(`${path} must be one of: ${allowedValues.join(", ")}`);
    }
    return parsed;
}
function hasOwn(record, key) {
    return Object.prototype.hasOwnProperty.call(record, key);
}
export function normalizeStudentNamePart(value) {
    if (typeof value !== "string") {
        return null;
    }
    const normalized = value.trim().replace(/\s+/g, " ");
    return normalized.length > 0 ? normalized : null;
}
export function composeStudentDisplayNameFromParts(familyName, givenName) {
    const normalizedFamilyName = normalizeStudentNamePart(familyName);
    const normalizedGivenName = normalizeStudentNamePart(givenName);
    if (!normalizedFamilyName || !normalizedGivenName) {
        return null;
    }
    return `${normalizedFamilyName} ${normalizedGivenName}`;
}
export function composeFamilyAccountDisplayNameFromStudentFamilyName(familyName) {
    const normalizedFamilyName = normalizeStudentNamePart(familyName);
    return normalizedFamilyName ? `${normalizedFamilyName}様` : null;
}
export function parseStudentStatus(value, path = "studentStatus") {
    return parseStringEnum(value, STUDENT_STATUSES, path);
}
export function parseFamilyAccountStatus(value, path = "familyAccountStatus") {
    return parseStringEnum(value, FAMILY_ACCOUNT_STATUSES, path);
}
export function parseEnrollmentType(value, path = "enrollmentType") {
    return parseStringEnum(value, ENROLLMENT_TYPES, path);
}
export function parseEnrollmentStatus(value, path = "enrollmentStatus") {
    return parseStringEnum(value, ENROLLMENT_STATUSES, path);
}
export function parseTrialPolicySnapshot(value, path = "trialPolicySnapshot") {
    const record = expectRecord(value, path);
    return {
        trialPolicyId: expectString(record.trialPolicyId, `${path}.trialPolicyId`)
    };
}
export function parseAdminStudent(value, path = "adminStudent") {
    const record = expectRecord(value, path);
    return {
        studentId: expectString(record.studentId, `${path}.studentId`),
        memberCode: expectString(record.memberCode, `${path}.memberCode`),
        familyName: expectOptionalString(record.familyName, `${path}.familyName`),
        givenName: expectOptionalString(record.givenName, `${path}.givenName`),
        displayName: expectString(record.displayName, `${path}.displayName`),
        displayNameKana: expectOptionalString(record.displayNameKana, `${path}.displayNameKana`),
        birthDate: expectString(record.birthDate, `${path}.birthDate`),
        status: parseStudentStatus(record.status, `${path}.status`),
        familyAccountId: expectOptionalString(record.familyAccountId, `${path}.familyAccountId`),
        familyAccountDisplayName: expectOptionalString(record.familyAccountDisplayName, `${path}.familyAccountDisplayName`),
        notesInternal: expectOptionalString(record.notesInternal, `${path}.notesInternal`),
        createdAt: expectString(record.createdAt, `${path}.createdAt`),
        updatedAt: expectString(record.updatedAt, `${path}.updatedAt`)
    };
}
export function parseAdminStudentsResponse(value, path = "adminStudentsResponse") {
    const record = expectRecord(value, path);
    return {
        items: expectArray(record.items, parseAdminStudent, `${path}.items`)
    };
}
export function parseCreateAdminStudentRequest(value, path = "createAdminStudentRequest") {
    const record = expectRecord(value, path);
    const createFamilyAccountRecord = record.createFamilyAccount === undefined || record.createFamilyAccount === null
        ? null
        : expectRecord(record.createFamilyAccount, `${path}.createFamilyAccount`);
    return {
        displayName: expectString(record.displayName, `${path}.displayName`),
        familyName: expectOptionalString(record.familyName, `${path}.familyName`),
        givenName: expectOptionalString(record.givenName, `${path}.givenName`),
        displayNameKana: expectOptionalString(record.displayNameKana, `${path}.displayNameKana`),
        birthDate: expectString(record.birthDate, `${path}.birthDate`),
        status: parseStudentStatus(record.status, `${path}.status`),
        familyAccountId: expectOptionalString(record.familyAccountId, `${path}.familyAccountId`),
        createFamilyAccount: createFamilyAccountRecord
            ? {
                displayName: expectString(createFamilyAccountRecord.displayName, `${path}.createFamilyAccount.displayName`),
                status: createFamilyAccountRecord.status === undefined
                    ? null
                    : parseFamilyAccountStatus(createFamilyAccountRecord.status, `${path}.createFamilyAccount.status`)
            }
            : null,
        notesInternal: expectOptionalString(record.notesInternal, `${path}.notesInternal`)
    };
}
export function parseUpdateAdminStudentRequest(value, path = "updateAdminStudentRequest") {
    const record = expectRecord(value, path);
    const parsed = {};
    if (hasOwn(record, "displayName")) {
        parsed.displayName = expectString(record.displayName, `${path}.displayName`);
    }
    if (hasOwn(record, "familyName")) {
        parsed.familyName = expectOptionalString(record.familyName, `${path}.familyName`);
    }
    if (hasOwn(record, "givenName")) {
        parsed.givenName = expectOptionalString(record.givenName, `${path}.givenName`);
    }
    if (hasOwn(record, "displayNameKana")) {
        parsed.displayNameKana = expectOptionalString(record.displayNameKana, `${path}.displayNameKana`);
    }
    if (hasOwn(record, "birthDate")) {
        parsed.birthDate = expectString(record.birthDate, `${path}.birthDate`);
    }
    if (hasOwn(record, "status")) {
        parsed.status = parseStudentStatus(record.status, `${path}.status`);
    }
    if (hasOwn(record, "familyAccountId")) {
        parsed.familyAccountId = expectOptionalString(record.familyAccountId, `${path}.familyAccountId`);
    }
    if (hasOwn(record, "notesInternal")) {
        parsed.notesInternal = expectOptionalString(record.notesInternal, `${path}.notesInternal`);
    }
    return parsed;
}
export function parseFamilyAccount(value, path = "familyAccount") {
    const record = expectRecord(value, path);
    return {
        familyAccountId: expectString(record.familyAccountId, `${path}.familyAccountId`),
        displayName: expectString(record.displayName, `${path}.displayName`),
        primaryContactPlatformUserId: expectOptionalString(record.primaryContactPlatformUserId, `${path}.primaryContactPlatformUserId`),
        primaryContactDisplayNameSnapshot: expectOptionalString(record.primaryContactDisplayNameSnapshot, `${path}.primaryContactDisplayNameSnapshot`),
        status: parseFamilyAccountStatus(record.status, `${path}.status`),
        createdAt: expectString(record.createdAt, `${path}.createdAt`),
        updatedAt: expectString(record.updatedAt, `${path}.updatedAt`)
    };
}
export function parseFamilyAccountsResponse(value, path = "familyAccountsResponse") {
    const record = expectRecord(value, path);
    return {
        items: expectArray(record.items, parseFamilyAccount, `${path}.items`)
    };
}
export function parseCreateFamilyAccountRequest(value, path = "createFamilyAccountRequest") {
    const record = expectRecord(value, path);
    return {
        displayName: expectString(record.displayName, `${path}.displayName`),
        primaryContactPlatformUserId: expectOptionalString(record.primaryContactPlatformUserId, `${path}.primaryContactPlatformUserId`),
        status: parseFamilyAccountStatus(record.status, `${path}.status`)
    };
}
export function parseUpdateFamilyAccountRequest(value, path = "updateFamilyAccountRequest") {
    const record = expectRecord(value, path);
    const parsed = {};
    if (hasOwn(record, "displayName")) {
        parsed.displayName = expectString(record.displayName, `${path}.displayName`);
    }
    if (hasOwn(record, "primaryContactPlatformUserId")) {
        parsed.primaryContactPlatformUserId = expectOptionalString(record.primaryContactPlatformUserId, `${path}.primaryContactPlatformUserId`);
    }
    if (hasOwn(record, "status")) {
        parsed.status = parseFamilyAccountStatus(record.status, `${path}.status`);
    }
    return parsed;
}
export function parseEnrollment(value, path = "enrollment") {
    const record = expectRecord(value, path);
    return {
        enrollmentId: expectString(record.enrollmentId, `${path}.enrollmentId`),
        revision: expectNumber(record.revision, `${path}.revision`),
        studentId: expectString(record.studentId, `${path}.studentId`),
        studentDisplayName: expectString(record.studentDisplayName, `${path}.studentDisplayName`),
        lessonId: expectString(record.lessonId, `${path}.lessonId`),
        lessonTitle: expectString(record.lessonTitle, `${path}.lessonTitle`),
        enrollmentType: parseEnrollmentType(record.enrollmentType, `${path}.enrollmentType`),
        status: parseEnrollmentStatus(record.status, `${path}.status`),
        joinDate: expectString(record.joinDate, `${path}.joinDate`),
        leaveDate: expectOptionalString(record.leaveDate, `${path}.leaveDate`),
        trialPolicySnapshot: expectNullable(record.trialPolicySnapshot, parseTrialPolicySnapshot, `${path}.trialPolicySnapshot`),
        convertedFromEnrollmentId: expectOptionalString(record.convertedFromEnrollmentId, `${path}.convertedFromEnrollmentId`),
        notesInternal: expectOptionalString(record.notesInternal, `${path}.notesInternal`),
        createdAt: expectString(record.createdAt, `${path}.createdAt`),
        updatedAt: expectString(record.updatedAt, `${path}.updatedAt`)
    };
}
export function parseEnrollmentsResponse(value, path = "enrollmentsResponse") {
    const record = expectRecord(value, path);
    return {
        items: expectArray(record.items, parseEnrollment, `${path}.items`)
    };
}
export function parseCreateEnrollmentRequest(value, path = "createEnrollmentRequest") {
    const record = expectRecord(value, path);
    return {
        studentId: expectString(record.studentId, `${path}.studentId`),
        lessonId: expectString(record.lessonId, `${path}.lessonId`),
        enrollmentType: parseEnrollmentType(record.enrollmentType, `${path}.enrollmentType`),
        status: expectNullable(record.status, parseEnrollmentStatus, `${path}.status`),
        joinDate: expectString(record.joinDate, `${path}.joinDate`),
        leaveDate: expectOptionalString(record.leaveDate, `${path}.leaveDate`),
        notesInternal: expectOptionalString(record.notesInternal, `${path}.notesInternal`)
    };
}
export function parseUpdateEnrollmentRequest(value, path = "updateEnrollmentRequest") {
    const record = expectRecord(value, path);
    const parsed = {};
    if (hasOwn(record, "status")) {
        parsed.status = parseEnrollmentStatus(record.status, `${path}.status`);
    }
    if (hasOwn(record, "joinDate")) {
        parsed.joinDate = expectString(record.joinDate, `${path}.joinDate`);
    }
    if (hasOwn(record, "leaveDate")) {
        parsed.leaveDate = expectOptionalString(record.leaveDate, `${path}.leaveDate`);
    }
    if (hasOwn(record, "notesInternal")) {
        parsed.notesInternal = expectOptionalString(record.notesInternal, `${path}.notesInternal`);
    }
    return parsed;
}
export function parseTransitionEnrollmentStatusRequest(value, path = "transitionEnrollmentStatusRequest") {
    const record = expectRecord(value, path);
    return {
        toStatus: parseEnrollmentStatus(record.toStatus, `${path}.toStatus`)
    };
}
export function parseConvertEnrollmentToRegularRequest(value, path = "convertEnrollmentToRegularRequest") {
    const record = expectRecord(value, path);
    return {
        joinDate: expectOptionalString(record.joinDate, `${path}.joinDate`),
        notesInternal: expectOptionalString(record.notesInternal, `${path}.notesInternal`)
    };
}
function parseDateString(value, path) {
    const parsed = expectString(value, path).trim();
    if (!/^\d{4}-\d{2}-\d{2}$/.test(parsed)) {
        throw new Error(`${path} must be a YYYY-MM-DD date`);
    }
    return parsed;
}
function parseOptionalDateString(value, path) {
    const parsed = expectOptionalString(value, path);
    if (parsed === null) {
        return null;
    }
    if (!/^\d{4}-\d{2}-\d{2}$/.test(parsed)) {
        throw new Error(`${path} must be a YYYY-MM-DD date`);
    }
    return parsed;
}
function parseStringArray(value, path) {
    return expectArray(value, expectString, path).map((item) => item.trim());
}
function parseStrictUniqueStringArray(value, path, maxLength) {
    const parsed = parseStringArray(value, path);
    if (parsed.length === 0) {
        throw new Error(`${path} must contain at least one item`);
    }
    if (parsed.length > maxLength) {
        throw new Error(`${path} must contain at most ${maxLength} items`);
    }
    if (parsed.some((item) => item.length === 0)) {
        throw new Error(`${path} must not contain blank IDs`);
    }
    if (new Set(parsed).size !== parsed.length) {
        throw new Error(`${path} must not contain duplicate IDs`);
    }
    return parsed;
}
export function parseBulkCreateEnrollmentsRequest(value, path = "bulkCreateEnrollmentsRequest") {
    const record = expectRecord(value, path);
    return {
        lessonId: expectString(record.lessonId, `${path}.lessonId`).trim(),
        studentIds: parseStrictUniqueStringArray(record.studentIds, `${path}.studentIds`, 200),
        joinDate: parseOptionalDateString(record.joinDate, `${path}.joinDate`),
        leaveDate: parseOptionalDateString(record.leaveDate, `${path}.leaveDate`),
        notesInternal: expectOptionalString(record.notesInternal, `${path}.notesInternal`)
    };
}
function parseBulkCreateEnrollmentSkipReason(value, path = "bulkCreateEnrollmentSkipReason") {
    const parsed = expectString(value, path);
    if (parsed !== "alreadyCurrentlyEnrolled" && parsed !== "studentInactive") {
        throw new Error(`${path} must be one of: alreadyCurrentlyEnrolled, studentInactive`);
    }
    return parsed;
}
export function parseBulkCreateEnrollmentSkippedItem(value, path = "bulkCreateEnrollmentSkippedItem") {
    const record = expectRecord(value, path);
    return {
        studentId: expectString(record.studentId, `${path}.studentId`),
        studentDisplayName: expectString(record.studentDisplayName, `${path}.studentDisplayName`),
        reason: parseBulkCreateEnrollmentSkipReason(record.reason, `${path}.reason`),
        existingEnrollmentId: expectOptionalString(record.existingEnrollmentId, `${path}.existingEnrollmentId`),
        existingEnrollmentStatus: expectNullable(record.existingEnrollmentStatus, parseEnrollmentStatus, `${path}.existingEnrollmentStatus`)
    };
}
export function parseBulkCreateEnrollmentsResponse(value, path = "bulkCreateEnrollmentsResponse") {
    const record = expectRecord(value, path);
    const enrollmentType = expectString(record.enrollmentType, `${path}.enrollmentType`);
    const statusMode = expectString(record.statusMode, `${path}.statusMode`);
    const effectiveJoinDateSource = expectString(record.effectiveJoinDateSource, `${path}.effectiveJoinDateSource`);
    if (enrollmentType !== "regular") {
        throw new Error(`${path}.enrollmentType must be regular`);
    }
    if (statusMode !== "auto") {
        throw new Error(`${path}.statusMode must be auto`);
    }
    if (effectiveJoinDateSource !== "explicit" &&
        effectiveJoinDateSource !== "lessonStartDate" &&
        effectiveJoinDateSource !== "tenantToday") {
        throw new Error(`${path}.effectiveJoinDateSource is invalid`);
    }
    return {
        lessonId: expectString(record.lessonId, `${path}.lessonId`),
        lessonTitle: expectString(record.lessonTitle, `${path}.lessonTitle`),
        enrollmentType,
        statusMode,
        effectiveJoinDate: parseDateString(record.effectiveJoinDate, `${path}.effectiveJoinDate`),
        effectiveJoinDateSource,
        leaveDate: parseOptionalDateString(record.leaveDate, `${path}.leaveDate`),
        requestedCount: expectNumber(record.requestedCount, `${path}.requestedCount`),
        createdCount: expectNumber(record.createdCount, `${path}.createdCount`),
        skippedCount: expectNumber(record.skippedCount, `${path}.skippedCount`),
        created: expectArray(record.created, parseEnrollment, `${path}.created`),
        skipped: expectArray(record.skipped, parseBulkCreateEnrollmentSkippedItem, `${path}.skipped`)
    };
}
export function parseBulkUpdateStudentStatusRequest(value, path = "bulkUpdateStudentStatusRequest") {
    const record = expectRecord(value, path);
    return {
        studentIds: parseStringArray(record.studentIds, `${path}.studentIds`),
        status: parseStudentStatus(record.status, `${path}.status`)
    };
}
export function parseBulkUpdateStudentStatusResponse(value, path = "bulkUpdateStudentStatusResponse") {
    const record = expectRecord(value, path);
    return {
        studentIds: parseStringArray(record.studentIds, `${path}.studentIds`),
        status: parseStudentStatus(record.status, `${path}.status`)
    };
}
export function parseBulkUpdateFamilyAccountStatusRequest(value, path = "bulkUpdateFamilyAccountStatusRequest") {
    const record = expectRecord(value, path);
    return {
        familyAccountIds: parseStringArray(record.familyAccountIds, `${path}.familyAccountIds`),
        status: parseFamilyAccountStatus(record.status, `${path}.status`)
    };
}
export function parseBulkUpdateFamilyAccountStatusResponse(value, path = "bulkUpdateFamilyAccountStatusResponse") {
    const record = expectRecord(value, path);
    return {
        familyAccountIds: parseStringArray(record.familyAccountIds, `${path}.familyAccountIds`),
        status: parseFamilyAccountStatus(record.status, `${path}.status`)
    };
}
function parseAdminRosterGuardianRelationType(value, path = "adminRosterGuardianRelationType") {
    const parsed = expectString(value, path);
    if (parsed !== "parent" && parsed !== "guardian") {
        throw new Error(`${path} must be one of: parent, guardian`);
    }
    return parsed;
}
function parseRosterStudentSummary(value, path) {
    const record = expectRecord(value, path);
    return {
        studentId: expectString(record.studentId, `${path}.studentId`),
        displayName: expectString(record.displayName, `${path}.displayName`),
        status: parseStudentStatus(record.status, `${path}.status`)
    };
}
function parseRosterFamilyAccountSummary(value, path) {
    const record = expectRecord(value, path);
    return {
        familyAccountId: expectString(record.familyAccountId, `${path}.familyAccountId`),
        displayName: expectString(record.displayName, `${path}.displayName`),
        status: parseFamilyAccountStatus(record.status, `${path}.status`)
    };
}
function parseAdminRosterGuardianSummary(value, path = "adminRosterGuardianSummary") {
    const record = expectRecord(value, path);
    return {
        platformUserId: expectString(record.platformUserId, `${path}.platformUserId`),
        displayName: expectString(record.displayName, `${path}.displayName`),
        memberStatus: expectNullable(record.memberStatus, parseMemberStatus, `${path}.memberStatus`),
        relationTypes: expectArray(record.relationTypes, parseAdminRosterGuardianRelationType, `${path}.relationTypes`),
        linkedStudentIds: expectArray(record.linkedStudentIds, expectString, `${path}.linkedStudentIds`)
    };
}
function parseStudentPrimaryContact(value, path) {
    if (value === null || value === undefined)
        return null;
    const record = expectRecord(value, path);
    return {
        platformUserId: expectString(record.platformUserId, `${path}.platformUserId`),
        displayName: expectString(record.displayName, `${path}.displayName`),
        memberStatus: expectNullable(record.memberStatus, parseMemberStatus, `${path}.memberStatus`),
        hasActiveLinkToThisStudent: expectBoolean(record.hasActiveLinkToThisStudent, `${path}.hasActiveLinkToThisStudent`)
    };
}
function parseFamilyPrimaryContact(value, path) {
    if (value === null || value === undefined)
        return null;
    const record = expectRecord(value, path);
    return {
        platformUserId: expectString(record.platformUserId, `${path}.platformUserId`),
        displayName: expectString(record.displayName, `${path}.displayName`),
        memberStatus: expectNullable(record.memberStatus, parseMemberStatus, `${path}.memberStatus`),
        hasActiveLinkToAnyFamilyStudent: expectBoolean(record.hasActiveLinkToAnyFamilyStudent, `${path}.hasActiveLinkToAnyFamilyStudent`)
    };
}
export function parseAdminStudentRosterRelation(value, path = "adminStudentRosterRelation") {
    const record = expectRecord(value, path);
    return {
        studentId: expectString(record.studentId, `${path}.studentId`),
        familyAccount: expectNullable(record.familyAccount, parseRosterFamilyAccountSummary, `${path}.familyAccount`),
        siblingStudents: expectArray(record.siblingStudents, parseRosterStudentSummary, `${path}.siblingStudents`),
        linkedGuardians: expectArray(record.linkedGuardians, (item, itemPath) => {
            const guardian = parseAdminRosterGuardianSummary(item, itemPath);
            const itemRecord = expectRecord(item, itemPath);
            return {
                ...guardian,
                isPrimaryContact: expectBoolean(itemRecord.isPrimaryContact, `${itemPath}.isPrimaryContact`)
            };
        }, `${path}.linkedGuardians`),
        primaryContact: parseStudentPrimaryContact(record.primaryContact, `${path}.primaryContact`)
    };
}
export function parseAdminFamilyAccountRosterRelation(value, path = "adminFamilyAccountRosterRelation") {
    const record = expectRecord(value, path);
    return {
        familyAccountId: expectString(record.familyAccountId, `${path}.familyAccountId`),
        students: expectArray(record.students, parseRosterStudentSummary, `${path}.students`),
        linkedGuardians: expectArray(record.linkedGuardians, (item, itemPath) => {
            const guardian = parseAdminRosterGuardianSummary(item, itemPath);
            const itemRecord = expectRecord(item, itemPath);
            return {
                ...guardian,
                isPrimaryContact: expectBoolean(itemRecord.isPrimaryContact, `${itemPath}.isPrimaryContact`)
            };
        }, `${path}.linkedGuardians`),
        primaryContact: parseFamilyPrimaryContact(record.primaryContact, `${path}.primaryContact`)
    };
}
export function parseAdminRosterRelationsResponse(value, path = "adminRosterRelationsResponse") {
    const record = expectRecord(value, path);
    return {
        studentRelations: expectArray(record.studentRelations, parseAdminStudentRosterRelation, `${path}.studentRelations`),
        familyAccountRelations: expectArray(record.familyAccountRelations, parseAdminFamilyAccountRosterRelation, `${path}.familyAccountRelations`)
    };
}
